import "./aiforme.scss";

import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { Button as StencilButton, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Container, Row } from "@amzn/stencil-react-components/layout";
import { RenderModalFunction, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { Text } from "@amzn/stencil-react-components/text";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import Link, { LinkTextDecorationOptions } from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { AiForMeSearchList } from "common/components/AIForMe/AiForMeSearchList";
import { Disclaimer } from "common/components/AIForMe/Disclaimer";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { ModalWhatsNew } from "common/components/ModalWhatsNew/ModalWhatsNew";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { AmazonianInformation } from "lib/models/oui-models";
import { UserPermissions } from "lib/services";
import { UserInformation } from "lib/services/user-information-service";
import React, { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

const TIMEOUT_TO_DISPLAY_DISCLAIMER = 500; // ms

const pageSectionPadding = {
  paddingTop: 32,
  paddingBottom: 32,
};

const AiForMePage: FC = () => {
  const history = useHistory();
  let openHandler: () => void | undefined;

  const { addErrorBanner } = useMessageBannersBox();
  const [userInfo, setUserInfo] = useState<AmazonianInformation | null>(null);
  const [isUserInfoLoaded, setIsUserInfoLoaded] = useState(false);

  useEffect(() => {
    document.title = getString("aiForMe.title");

    UserInformation.current
      .getUserInformation()
      .then((info) => {
        setUserInfo(info);
      })
      .catch((error) => {
        console.error("Error fetching user information:", error);
      })
      .finally(() => {
        setIsUserInfoLoaded(true);
      });
  }, []);

  // Check permissions on component mount
  useEffect(() => {
    if (!UserPermissions.current.hasAuthorized) {
      // User hasn't been authorized yet, wait for authorization
      return;
    }

    if (!UserPermissions.current.hasPermission(PERMISSIONS.AI_FOR_ME)) {
      // Only show error if we have valid permissions but user doesn't have this specific permission
      // If permissions are expired, the UserPermissions service will handle the session expired redirect
      const hasValidPermissions = UserPermissions.current.permissions.length > 0;
      if (hasValidPermissions) {
        addErrorBanner(getString("errors.notAuthorized"), false);
        history.push("/");
      }
    }
  }, []);

  const renderModal: RenderModalFunction = ({ close }: RenderModalFunctionParams) => <Disclaimer close={close} />;

  // TODO uncomment this block to enable disclaimer on load
  // setTimeout(() => {
  //   if (openHandler) {
  //     openHandler();
  //   }
  // }, TIMEOUT_TO_DISPLAY_DISCLAIMER);

  return (
    <WithModal renderModal={renderModal}>
      {({ open }) => (
        <>
          <ModalWhatsNew />
          <div className="aiforme-gradient-back">
            <PageMainContainer>
              <Row>
                <Breadcrumbs
                  crumbs={[
                    {
                      title: getString("logoText"),
                      url: "/",
                    },
                    {
                      title: getString("aiForMe.title"),
                    },
                  ]}
                  onCrumbClick={
                    /* istanbul ignore next */
                    (e, crumb: CrumbProps) => {
                      if (crumb.url) {
                        e.preventDefault();
                        history.push(crumb.url);
                      }
                    }
                  }
                />
              </Row>
              <Row justifyContent="space-between" alignItems="flex-end" style={{ minHeight: "270px" }}>
                <Col flex={2}>
                  <Text data-cy="welcome-title" fontSize={"T500"} fontWeight="bold" style={{ paddingBottom: 20 }}>
                    {isUserInfoLoaded &&
                      (userInfo?.basicInfo?.firstName
                        ? getString("aiForMe.discoverAiToolsWithName", { amazonianName: userInfo?.basicInfo.firstName })
                        : getString("aiForMe.discoverAiToolsWithoutName"))}
                  </Text>
                  <Text style={{ paddingBottom: 20 }}>
                    {getString("aiForMe.findBestTools")}
                    <br />
                    {getString("aiForMe.boostProductivity")}
                  </Text>
                  <Row columnGap={20} padding={{ bottom: 45 }}>
                    <Button
                      variant={ButtonVariant.Primary}
                      onClick={
                        /* istanbul ignore next */
                        () => {
                          history.push(Paths.aiformeIntake.url);
                        }
                      }
                      touchpoint="contribute-a-tool"
                    >
                      {getString("aiForMe.contributeTool")}
                    </Button>
                    <Link
                      href="https://amazon.sharepoint.com/:u:/s/ai-enablement-partners/EQ1dQdhiM4FGqAgeRw6qEswB39BHn6HD2X0xlK96WYrKIw?e=0DuZHQ"
                      target="_blank"
                      textDecoration={LinkTextDecorationOptions.None}
                      data-testid="learn-about-ai-button"
                      touchpoint="learn-about-ai-button"
                    >
                      <StencilButton>{getString("aiForMe.learnAboutAI")}</StencilButton>
                    </Link>
                  </Row>
                </Col>
                <Col flex={1}>
                  <img
                    src="/img/aiforme-people-animated.svg"
                    alt="Two people working using AI tools"
                    style={{
                      maxWidth: 468,
                      width: "100%",
                      height: "auto",
                    }}
                    data-testid="people-img"
                  />
                </Col>
              </Row>
            </PageMainContainer>
          </div>
          <div className="aiforme-content">
            <PageMainContainer>
              <Container {...pageSectionPadding}>
                <AiForMeSearchList />
              </Container>
            </PageMainContainer>
          </div>
          {
            // Open the modal on load
            (openHandler = open)
          }
        </>
      )}
    </WithModal>
  );
};

export default AiForMePage;
